import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";
import {
  BarChart3,
  Brain,
  Building2,
  Calendar,
  ClipboardList,
  DollarSign,
  FileText,
  Heart,
  Home,
  Search,
  Settings,
  TrendingUp,
  Users,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

interface CommandItem {
  id: string;
  label: string;
  description?: string;
  icon: React.ComponentType<{ className?: string }>;
  href: string;
  roles?: string[];
}

const commands: CommandItem[] = [
  {
    id: "dashboard",
    label: "Dashboard",
    description: "Visão geral",
    icon: Home,
    href: "/dashboard",
  },
  {
    id: "agenda",
    label: "Agenda",
    description: "Consultas e horários",
    icon: Calendar,
    href: "/agenda",
    roles: ["ADMIN", "PSICOLOGA", "RECEPCAO"],
  },
  {
    id: "pacientes",
    label: "Pacientes",
    description: "Lista de pacientes",
    icon: Users,
    href: "/pacientes",
    roles: ["ADMIN", "PSICOLOGA", "RECEPCAO"],
  },
  {
    id: "prontuario",
    label: "Prontuários",
    description: "Registros clínicos",
    icon: ClipboardList,
    href: "/prontuario",
    roles: ["ADMIN", "PSICOLOGA"],
  },
  {
    id: "escalas",
    label: "Escalas",
    description: "Instrumentos psicológicos",
    icon: Brain,
    href: "/escalas",
    roles: ["ADMIN", "PSICOLOGA"],
  },
  {
    id: "documentos",
    label: "Documentos",
    description: "Laudos e relatórios",
    icon: FileText,
    href: "/documentos",
    roles: ["ADMIN", "PSICOLOGA"],
  },
  {
    id: "financeiro",
    label: "Financeiro",
    description: "Pagamentos e faturamento",
    icon: DollarSign,
    href: "/financeiro",
    roles: ["ADMIN", "PSICOLOGA"],
  },
  {
    id: "nr1",
    label: "NR-1",
    description: "Saúde ocupacional",
    icon: BarChart3,
    href: "/nr1",
    roles: ["ADMIN", "PSICOLOGA"],
  },
  {
    id: "contratos",
    label: "Contratos",
    description: "Gestão de contratos",
    icon: FileText,
    href: "/contratos",
    roles: ["ADMIN", "PSICOLOGA"],
  },
  {
    id: "produtividade",
    label: "Produtividade",
    description: "Métricas e desempenho",
    icon: TrendingUp,
    href: "/produtividade",
    roles: ["ADMIN", "PSICOLOGA"],
  },
  {
    id: "meu-espaco",
    label: "Meu Espaço",
    description: "Portal do paciente",
    icon: Heart,
    href: "/meu-espaco",
    roles: ["PACIENTE", "FAMILIAR"],
  },
  {
    id: "usuarios",
    label: "Usuários",
    description: "Gerenciar usuários",
    icon: Settings,
    href: "/admin/usuarios",
    roles: ["ADMIN"],
  },
  {
    id: "empresas",
    label: "Empresas",
    description: "Gerenciar empresas",
    icon: Building2,
    href: "/admin/empresas",
    roles: ["ADMIN", "PSICOLOGA"],
  },
];

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  const userRole = user?.role || "";

  const filtered = useMemo(() => {
    const available = commands.filter(
      (cmd) => !cmd.roles || cmd.roles.includes(userRole),
    );
    if (!query.trim()) return available;
    const q = query.toLowerCase();
    return available.filter(
      (cmd) =>
        cmd.label.toLowerCase().includes(q) ||
        cmd.description?.toLowerCase().includes(q),
    );
  }, [query, userRole]);

  const handleSelect = useCallback(
    (item: CommandItem) => {
      setOpen(false);
      setQuery("");
      navigate(item.href);
    },
    [navigate],
  );

  // Keyboard shortcut: Cmd+K / Ctrl+K
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  // Arrow keys + Enter navigation
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((prev) => Math.min(prev + 1, filtered.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((prev) => Math.max(prev - 1, 0));
      } else if (e.key === "Enter" && filtered[selectedIndex]) {
        e.preventDefault();
        handleSelect(filtered[selectedIndex]);
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, filtered, selectedIndex, handleSelect]);

  // Reset selection on query change
  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  // Scroll selected item into view
  useEffect(() => {
    if (!listRef.current) return;
    const items = listRef.current.querySelectorAll("[data-command-item]");
    items[selectedIndex]?.scrollIntoView({ block: "nearest" });
  }, [selectedIndex]);

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) setQuery("");
      }}
    >
      <DialogContent className="sm:max-w-[480px] p-0 gap-0 overflow-hidden">
        <DialogHeader className="sr-only">
          <DialogTitle>Paleta de comandos</DialogTitle>
          <DialogDescription>
            Navegue rapidamente pelo sistema
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center border-b px-3">
          <Search className="h-4 w-4 text-slate-400 shrink-0" />
          <Input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar páginas..."
            className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0 h-12"
            autoFocus
          />
          <kbd className="hidden sm:inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
            ESC
          </kbd>
        </div>
        <div
          ref={listRef}
          className="max-h-[300px] overflow-y-auto p-2"
          role="listbox"
          aria-label="Resultados da busca"
        >
          {filtered.length === 0 ? (
            <p className="py-6 text-center text-sm text-slate-500">
              Nenhum resultado encontrado.
            </p>
          ) : (
            filtered.map((item, index) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  data-command-item
                  role="option"
                  aria-selected={index === selectedIndex}
                  onClick={() => handleSelect(item)}
                  onMouseEnter={() => setSelectedIndex(index)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left text-sm transition-colors ${
                    index === selectedIndex
                      ? "bg-brand-azul-profundo/10 text-brand-azul-profundo"
                      : "text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <span className="font-medium">{item.label}</span>
                    {item.description && (
                      <span className="ml-2 text-xs text-slate-400">
                        {item.description}
                      </span>
                    )}
                  </div>
                </button>
              );
            })
          )}
        </div>
        <div className="border-t p-2 flex items-center justify-between text-xs text-slate-400">
          <span>↑↓ navegar • Enter selecionar • Esc fechar</span>
          <Button
            variant="ghost"
            size="sm"
            className="text-xs h-6 px-2"
            onClick={() => setOpen(false)}
          >
            Fechar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
